"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

export type GaEventParams = Record<string, unknown>;

type GtagFn = (...args: unknown[]) => void;

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: GtagFn;
};

type PendingEvent =
  | { method: "event"; args: [string, GaEventParams | undefined] }
  | { method: "pageView"; args: [] };

type ReadySubscriber = (isReady: boolean) => void;

let isGtagReady = false;
let activeMeasurementId: string | null = null;
let consentOverride: boolean | null = null;
const pendingEvents: PendingEvent[] = [];
// Events queued while consent is not yet granted. Flushed once consent is
// granted (see configure()) instead of being dropped — capped since this is
// in-memory, per-page-load state that could otherwise grow unbounded across
// a long consent-less browsing session.
const consentPendingEvents: PendingEvent[] = [];
const MAX_CONSENT_PENDING_EVENTS = 20;
const readySubscribers = new Set<ReadySubscriber>();

const HAS_ACCEPTED_COOKIES_KEY = "hasAcceptedCookies";
const GTAG_SCRIPT_ID = "studio-ga-gtag";

const parseConsentValue = (value: string | null | undefined) => {
  if (!value) {
    return false;
  }

  const normalized = value.toLowerCase();
  return (
    normalized === "true" || normalized === "1" || normalized === "granted"
  );
};

const getCookie = (name: string) => {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const cookieString = document.cookie;
    if (!cookieString) return null;

    const prefix = `${encodeURIComponent(name)}=`;
    const cookies = cookieString.split(";");
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length));
      }
    }
  } catch (_) {
    return null;
  }

  return null;
};

const getLocalStorageItem = (key: string) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const hasConsent = () => {
  if (typeof consentOverride === "boolean") {
    return consentOverride;
  }

  const cookieValue = getCookie(HAS_ACCEPTED_COOKIES_KEY);
  if (cookieValue !== null) {
    return parseConsentValue(cookieValue);
  }

  const localStorageValue = getLocalStorageItem(HAS_ACCEPTED_COOKIES_KEY);
  if (localStorageValue !== null) {
    return parseConsentValue(localStorageValue);
  }

  return false;
};

const notifyReadySubscribers = (ready: boolean) => {
  readySubscribers.forEach((subscriber) => {
    try {
      subscriber(ready);
    } catch (error) {
      console.error("Google Analytics subscriber failed", error);
    }
  });
};

const getGtag = (): GtagFn | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return (window as GtagWindow).gtag ?? null;
};

const buildPageViewParams = (): GaEventParams => ({
  page_path: `${window.location.pathname}${window.location.search}`,
  page_location: window.location.href,
  page_title: document.title,
});

const dispatchEvent = (event: PendingEvent) => {
  const gtag = getGtag();
  if (!gtag) return;

  if (event.method === "event") {
    const [name, params] = event.args;
    gtag("event", name, params ?? {});
  } else {
    gtag("event", "page_view", buildPageViewParams());
  }
};

const flushPendingEvents = () => {
  if (!isGtagReady) {
    return;
  }

  while (pendingEvents.length) {
    const event = pendingEvents.shift();
    if (!event) continue;
    dispatchEvent(event);
  }
};

const resetGtagState = () => {
  isGtagReady = false;
  pendingEvents.length = 0;
  notifyReadySubscribers(false);
};

const ensureGtagInitialized = () => {
  if (
    isGtagReady ||
    !activeMeasurementId ||
    typeof window === "undefined" ||
    !hasConsent()
  ) {
    return;
  }

  try {
    const w = window as GtagWindow;
    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
      w.gtag = function gtag() {
        // gtag.js consumes queued dataLayer entries once the script loads, so
        // events fired before load are not lost.
        w.dataLayer!.push(arguments);
      };
    }

    w.gtag("js", new Date());
    w.gtag("config", activeMeasurementId, { send_page_view: false });

    if (!document.getElementById(GTAG_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = GTAG_SCRIPT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        activeMeasurementId,
      )}`;
      script.onerror = () => {
        console.error("Failed to load Google Analytics gtag.js script");
      };
      document.head.appendChild(script);
    }

    isGtagReady = true;
    notifyReadySubscribers(true);
    flushPendingEvents();
  } catch (error) {
    console.error("Failed to initialize Google Analytics", error);
  }
};

const enqueueOrDispatchEvent = (event: PendingEvent) => {
  if (!activeMeasurementId || typeof window === "undefined") {
    return;
  }

  if (!hasConsent()) {
    consentPendingEvents.push(event);
    if (consentPendingEvents.length > MAX_CONSENT_PENDING_EVENTS) {
      consentPendingEvents.shift();
    }
    return;
  }

  if (isGtagReady) {
    dispatchEvent(event);
    return;
  }

  pendingEvents.push(event);
  ensureGtagInitialized();
};

const flushConsentPendingEvents = () => {
  if (!hasConsent()) {
    return;
  }

  while (consentPendingEvents.length) {
    const event = consentPendingEvents.shift();
    if (!event) continue;
    enqueueOrDispatchEvent(event);
  }
};

const googleAnalyticsService = {
  isReady: () => isGtagReady,
  configure: ({
    measurementId,
    consentGranted,
  }: {
    measurementId?: string | null;
    consentGranted?: boolean;
  }) => {
    const normalizedId = measurementId?.trim() || null;
    const nextConsent =
      typeof consentGranted === "boolean" ? consentGranted : consentOverride;
    const idChanged = normalizedId !== activeMeasurementId;
    const consentChanged = nextConsent !== consentOverride;

    if (idChanged || consentChanged) {
      const consentNewlyGranted = consentChanged && nextConsent === true;
      activeMeasurementId = normalizedId;
      consentOverride = nextConsent ?? null;
      if (!activeMeasurementId || !hasConsent() || idChanged) {
        resetGtagState();
      }
      if (consentNewlyGranted) {
        flushConsentPendingEvents();
      }
    }
  },
  ensureInitialized: ensureGtagInitialized,
  subscribe: (subscriber: ReadySubscriber) => {
    readySubscribers.add(subscriber);
    return () => {
      readySubscribers.delete(subscriber);
    };
  },
  event: (name: string, params?: GaEventParams) =>
    enqueueOrDispatchEvent({ method: "event", args: [name, params] }),
  pageView: () => enqueueOrDispatchEvent({ method: "pageView", args: [] }),
};

// Hook API aligned with useFacebookPixel (components/facebook-pixel.tsx).
// Event names follow GA4 recommended events where they exist; custom events
// use snake_case.
export const useGoogleAnalytics = () => {
  const [isReady, setIsReady] = useState(googleAnalyticsService.isReady());

  useEffect(() => {
    const unsubscribe = googleAnalyticsService.subscribe(setIsReady);
    googleAnalyticsService.ensureInitialized();
    return unsubscribe;
  }, []);

  return useMemo(
    () => ({
      isReady,
      event: (name: string, params?: GaEventParams) =>
        googleAnalyticsService.event(name, params),
    }),
    [isReady],
  );
};

export const GoogleAnalytics = ({
  measurementId,
  consentGranted,
}: {
  measurementId: string;
  consentGranted?: boolean;
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString();
  const previousConsent = useRef(consentGranted);

  useEffect(() => {
    const wasGranted = previousConsent.current;
    previousConsent.current = consentGranted;

    googleAnalyticsService.configure({ measurementId, consentGranted });
    googleAnalyticsService.ensureInitialized();
    if (consentGranted && !wasGranted) {
      googleAnalyticsService.pageView();
    }
  }, [measurementId, consentGranted]);

  useEffect(() => {
    if (!pathname) return;
    googleAnalyticsService.pageView();
  }, [pathname, search]);

  return null;
};
